import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { lessonRunsApi } from '../../../services/apiV2';
import { useLessonControlData } from '../../../hooks/v2/useLessonControlData';
import { StepRenderer } from '../../../components/v2/StepRenderer';
import { ExplainBroadcastContent } from '../../../components/v2/ExplainBroadcastContent';
import { PacingTimerCard } from '../../../components/v2/PacingTimerCard';
import { PacingTimerRuntimeState } from '../../../utils/pacingTimer';
import { publishLessonRunSync, subscribeLessonRunSync } from '../../../utils/lessonRunSync';
import { matchingPairsToTextLines, matchingValueText, normalizeMatchingDraftRows } from '../../../utils/matchingPairs';
import { presentationSlideImageUrl } from '../../../utils/lessonResources';

const markdownComponents = {
  p({ children }: any) {
    return <p className="my-2 leading-relaxed">{children}</p>;
  },
  ul({ children }: any) {
    return <ul className="my-2 list-disc pl-6 space-y-1">{children}</ul>;
  },
  ol({ children }: any) {
    return <ol className="my-2 list-decimal pl-6 space-y-1">{children}</ol>;
  },
  li({ children }: any) {
    return <li className="leading-relaxed">{children}</li>;
  },
  code({ inline, children }: any) {
    return !inline ? (
      <pre className="overflow-x-auto rounded bg-slate-950 p-3 text-xs text-slate-100">
        <code>{children}</code>
      </pre>
    ) : (
      <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-900">{children}</code>
    );
  },
};

const normalizeSignalType = (signalConfig: any): string => (
  String(signalConfig?.signal_type || signalConfig?.type || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
);

const normalizeTextList = (values: any): string[] => (
  Array.isArray(values)
    ? values.map((item: any) => matchingValueText(item).trim()).filter(Boolean)
    : []
);

const normalizeOptions = (values: any): string[] => (
  Array.isArray(values)
    ? values
      .map((item: any) => {
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object' && item !== null && typeof item.text === 'string') return item.text.trim();
        return String(item ?? '').trim();
      })
      .filter(Boolean)
    : []
);

const normalizeText = (value: any): string => String(value ?? '').trim();

type GuidedSequenceDisplayCard = {
  key: string;
  prompt: string;
  required: boolean;
  template: string;
};

const sanitizeStepContent = (content: any) => {
  if (!content || typeof content !== 'object' || Array.isArray(content)) return content;
  const nextContent = { ...content };
  delete nextContent.teacher_notes;
  delete nextContent.teacherNotes;
  return nextContent;
};

const buildAnswerKeyView = (answerKey: any) => {
  if (!answerKey || typeof answerKey !== 'object') return null;

  if (answerKey.kind === 'mcq_single') {
    const option = (answerKey.options || [])[Number(answerKey.correct_index)];
    const optionText = typeof option === 'string' ? option : (option?.text || '');
    return (
      <p className="text-sm text-emerald-900">
        Correct option: {optionText || `#${answerKey.correct_index}`}
      </p>
    );
  }

  if (answerKey.kind === 'mcq_multi') {
    return (
      <ul className="list-disc pl-5 text-sm text-emerald-900">
        {(Array.isArray(answerKey.correct_indices) ? answerKey.correct_indices : []).map((idx: number) => {
          const option = (answerKey.options || [])[idx];
          const optionText = typeof option === 'string' ? option : (option?.text || '');
          return (
            <li key={`answer-key-${idx}`}>
              {optionText || `#${idx}`}
            </li>
          );
        })}
      </ul>
    );
  }

  if (answerKey.kind === 'text') {
    return (
      <pre className="overflow-x-auto rounded bg-slate-950 p-3 text-sm text-emerald-100">
        <code>{String(answerKey.answer_code || '')}</code>
      </pre>
    );
  }

  if (answerKey.kind === 'matching') {
    return (
      <ul className="list-disc pl-5 text-sm text-emerald-900">
        {matchingPairsToTextLines(answerKey.pairs || {}).map((line) => (
          <li key={`answer-pair-${line}`}>{line}</li>
        ))}
      </ul>
    );
  }

  if (answerKey.kind === 'ordering') {
    return (
      <ol className="list-decimal pl-5 text-sm text-emerald-900">
        {(Array.isArray(answerKey.answer) ? answerKey.answer : []).map((item: any, idx: number) => (
          <li key={`answer-order-${idx}`}>{String(item)}</li>
        ))}
      </ol>
    );
  }

  if (answerKey.kind === 'drag_drop' && Array.isArray(answerKey.answer_tokens)) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-emerald-900">Expected tokens</p>
        <div className="flex flex-wrap gap-2">
          {answerKey.answer_tokens.map((token: any, idx: number) => (
            <span
              key={`answer-token-${idx}`}
              className="rounded border border-emerald-300 bg-white px-2 py-1 text-sm text-emerald-900"
            >
              {String(token ?? '') || '(blank)'}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (answerKey.kind === 'drag_drop_fill_blanks' || answerKey.kind === 'drag_drop_keywords') {
    return (
      <div className="space-y-2 text-sm text-emerald-900">
        <p>Answer: {Array.isArray(answerKey.answer) ? answerKey.answer.join(', ') : String(answerKey.answer || '')}</p>
        {answerKey.code ? (
          <pre className="overflow-x-auto rounded bg-slate-950 p-3 text-sm text-emerald-100">
            <code>{String(answerKey.code)}</code>
          </pre>
        ) : null}
        {answerKey.code_skeleton ? (
          <pre className="overflow-x-auto rounded bg-slate-950 p-3 text-sm text-emerald-100">
            <code>{String(answerKey.code_skeleton)}</code>
          </pre>
        ) : null}
      </div>
    );
  }

  const fallbackAnswer = Array.isArray(answerKey.answer)
    ? answerKey.answer.join(', ')
    : String(answerKey.answer || '');

  if (!fallbackAnswer) return null;
  return <p className="text-sm text-emerald-900">Answer: {fallbackAnswer}</p>;
};

type TeacherOpener = Pick<Window, 'closed' | 'focus'>;

export const focusTeacherOpener = (displaySource: string | null, opener: TeacherOpener | null): boolean => {
  if (!['lesson-control', 'teach'].includes(displaySource || '') || !opener) return false;
  try {
    if (opener.closed) return false;
    opener.focus();
    return true;
  } catch {
    return false;
  }
};

export const V2LessonDisplay: React.FC<{ classroomRunId?: number; onNextActivity?: () => void; nextActivityLabel?: string; transitionBusy?: boolean; contentOnly?: boolean; previewStepId?: number }> = ({ classroomRunId, onNextActivity, nextActivityLabel, transitionBusy, contentOnly, previewStepId }) => {
  const { runId: routeRunId } = useParams();
  const runId = classroomRunId ? String(classroomRunId) : routeRunId;
  const location = useLocation();
  const navigate = useNavigate();
  const normalizeStudentIds = useCallback((raw: any[]) => (
    Array.from(
      new Set(
        (Array.isArray(raw) ? raw : [])
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0),
      ),
    )
  ), []);
  const {
    controlData,
    liveSignalStats,
    loading,
    error,
    loadControl,
  } = useLessonControlData({
    runId,
    normalizeStudentIds,
    enableTrackAssignment: false,
    enableLiveSignalStats: true,
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'previous' | 'advance' | 'studio' | 'reveal' | null>(null);
  const [pacingTimerLoading, setPacingTimerLoading] = useState(false);
  const [pacingTimerError, setPacingTimerError] = useState<string | null>(null);
  const [guidedCardIndex, setGuidedCardIndex] = useState(0);
  const runIdNumber = runId ? Number(runId) : null;

  const currentStepId = useMemo(() => {
    if (previewStepId) return previewStepId;
    if (!controlData) return null;
    return controlData.display_step_id
      ?? controlData.current_step_id
      ?? controlData.target_step_id
      ?? controlData.run.class_current_step
      ?? null;
  }, [controlData, previewStepId]);

  const currentStepIndex = useMemo(() => {
    if (!controlData || !currentStepId) return -1;
    return controlData.steps.findIndex((step) => step.id === currentStepId);
  }, [controlData, currentStepId]);

  const currentStep = useMemo(() => {
    if (!controlData) return null;
    return controlData.steps[currentStepIndex] || null;
  }, [controlData, currentStepIndex]);
  const classStepId = controlData?.current_step_id
    ?? controlData?.target_step_id
    ?? controlData?.run.class_current_step
    ?? null;
  const classStep = useMemo(() => {
    if (!controlData || !classStepId) return null;
    return controlData.steps.find((step) => Number(step.id) === Number(classStepId)) || null;
  }, [classStepId, controlData]);
  const isSimulationActivity = currentStep?.is_simulation_activity === true;
  const simulationObjective = useMemo(() => {
    if (!isSimulationActivity) return '';
    const objective = currentStep?.content?.simulation?.presentation?.objective;
    return typeof objective === 'string' ? objective.trim() : '';
  }, [currentStep?.content, isSimulationActivity]);
  const currentStepStartedAt = useMemo(() => {
    if (!controlData) return null;
    const liveResource = (controlData.run.live_resource && typeof controlData.run.live_resource === 'object')
      ? controlData.run.live_resource as Record<string, any>
      : {};
    return controlData.display_step_started_at
      || controlData.run.current_step_started_at
      || liveResource.current_step_started_at
      || null;
  }, [controlData]);
  const currentStepTimer = useMemo(() => {
    if (!controlData || !currentStep) return null;
    const direct = (controlData.run as any).current_step_timer;
    if (direct && Number(direct.step_id) === Number(currentStep.id)) return direct;
    const liveResource = (controlData.run.live_resource && typeof controlData.run.live_resource === 'object')
      ? controlData.run.live_resource as Record<string, any>
      : {};
    const fallback = liveResource.pacing_timer;
    return fallback && Number(fallback.step_id) === Number(currentStep.id) ? fallback : null;
  }, [controlData, currentStep]);

  const previousStep = useMemo(() => {
    if (!controlData || currentStepIndex <= 0) return null;
    return controlData.steps[currentStepIndex - 1] || null;
  }, [controlData, currentStepIndex]);

  const nextStep = useMemo(() => {
    if (!controlData) return null;
    return controlData.steps[currentStepIndex + 1] || null;
  }, [controlData, currentStepIndex]);

  const liveResource = useMemo(() => {
    const raw = controlData?.run?.live_resource;
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, any> : {};
  }, [controlData?.run?.live_resource]);

  const activePresentation = useMemo(() => {
    const state = liveResource.presentation;
    if (!state || typeof state !== 'object' || !state.display_active) return null;
    const resource = (controlData?.resources || []).find((item: any) => (
      String(item.src || '') === String(state.src || '')
      && String(item.title || '') === String(state.title || '')
    ));
    if (!resource || resource.presentation_mode !== 'selected_slides' || !resource.selected_slides?.length) return null;
    const index = Math.max(0, Math.min(Number(state.slide_index) || 0, resource.selected_slides.length - 1));
    return { resource, slide: resource.selected_slides[index], index, slideCount: resource.selected_slides.length };
  }, [controlData?.resources, liveResource]);

  const currentStepRevealed = useMemo(() => {
    const revealedSteps = liveResource.revealed_steps;
    if (!currentStep || !revealedSteps || typeof revealedSteps !== 'object') return false;
    return Boolean((revealedSteps as Record<string, any>)[String(currentStep.id)]);
  }, [currentStep, liveResource]);

  const activeExplainBroadcast = useMemo(() => {
    const broadcast = liveResource.broadcast;
    if (!broadcast || typeof broadcast !== 'object') return null;
    if (broadcast.kind !== 'explain') return null;
    if (Number(broadcast.step_id) !== Number(currentStepId)) return null;
    return broadcast;
  }, [currentStepId, liveResource]);

  const currentStepSignalConfig = useMemo(() => {
    if (!currentStep) return {};
    const raw = (currentStep as any).signal_config;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return raw;
  }, [currentStep]);

  const currentStepEvidenceConfig = useMemo(() => {
    const raw = (currentStep as any)?.evidence_config;
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  }, [currentStep]);

  const sourceItem = useMemo(() => {
    const content = currentStep?.content;
    if (!content || typeof content !== 'object' || Array.isArray(content)) return null;
    return (content.source_item && typeof content.source_item === 'object')
      ? content.source_item
      : ((content.sourceItem && typeof content.sourceItem === 'object') ? content.sourceItem : null);
  }, [currentStep?.content]);

  const signalType = normalizeSignalType(currentStepSignalConfig);
  const isMcqSignal = signalType === 'mcq' || signalType === 'mcq_single' || signalType === 'mcq_multi';
  const isGuidedSequenceSignal = signalType === 'guided_sequence';
  const isMatchingSignal = signalType === 'matching' || signalType === 'pairs' || String(sourceItem?.type || '').trim().toLowerCase() === 'matching';
  const isOrderingSignal = signalType === 'ordering' || String(sourceItem?.type || '').trim().toLowerCase() === 'ordering';
  const isDragDropSignal = signalType === 'drag_drop_code'
    || signalType === 'drag_drop_fill_blanks'
    || String(sourceItem?.type || '').trim().toLowerCase() === 'drag_drop_code'
    || String(sourceItem?.type || '').trim().toLowerCase() === 'drag_drop_fill_blanks';

  const signalPrompt = useMemo(() => {
    if (typeof currentStepSignalConfig?.prompt === 'string') return currentStepSignalConfig.prompt;
    if (typeof currentStepSignalConfig?.question === 'string') return currentStepSignalConfig.question;
    if (typeof (sourceItem as any)?.question === 'string') return (sourceItem as any).question;
    return '';
  }, [currentStepSignalConfig, sourceItem]);

  const guidedSequenceCards = useMemo<GuidedSequenceDisplayCard[]>(() => {
    if (!isGuidedSequenceSignal) return [];
    const cardsRaw = Array.isArray(currentStepSignalConfig?.cards) ? currentStepSignalConfig.cards : [];
    return cardsRaw
      .map((card: any, idx: number) => {
        if (!card || typeof card !== 'object') return null;
        const key = normalizeText(card.key);
        if (!key) return null;
        const prompt = normalizeText(card.prompt) || `Prompt ${idx + 1}`;
        const template = typeof card.template === 'string' ? card.template : '';
        return {
          key,
          prompt,
          required: Boolean(card.required),
          template,
        };
      })
      .filter((card: GuidedSequenceDisplayCard | null): card is GuidedSequenceDisplayCard => Boolean(card));
  }, [currentStepSignalConfig?.cards, isGuidedSequenceSignal]);

  const signalOptions = useMemo(
    () => normalizeOptions(
      currentStepSignalConfig?.options
      || currentStepSignalConfig?.choices
      || (sourceItem as any)?.options
      || (sourceItem as any)?.choices,
    ),
    [currentStepSignalConfig, sourceItem],
  );
  const matchingLeftItems = useMemo(() => {
    const direct = normalizeTextList((sourceItem as any)?.left_items);
    if (direct.length > 0) return direct;
    return normalizeMatchingDraftRows((sourceItem as any)?.pairs)
      .map((row) => row.left.trim())
      .filter(Boolean);
  }, [sourceItem]);
  const matchingRightItems = useMemo(() => {
    const direct = normalizeTextList((sourceItem as any)?.right_items);
    if (direct.length > 0) return direct;
    return Array.from(new Set(
      normalizeMatchingDraftRows((sourceItem as any)?.pairs)
        .map((row) => row.right.trim())
        .filter(Boolean),
    ));
  }, [sourceItem]);
  const orderingItems = useMemo(() => normalizeTextList((sourceItem as any)?.items), [sourceItem]);
  const dragDropTokens = useMemo(() => {
    if (!isDragDropSignal) return [];
    return normalizeTextList((sourceItem as any)?.tokens || (sourceItem as any)?.options);
  }, [isDragDropSignal, sourceItem]);
  const dragDropTarget = useMemo(() => {
    if (!isDragDropSignal) return '';
    return String((sourceItem as any)?.target || (sourceItem as any)?.code || (sourceItem as any)?.template || '').trim();
  }, [isDragDropSignal, sourceItem]);
  const dragDropSegments = dragDropTarget ? dragDropTarget.split('____') : [];
  const dragDropBlankCount = Math.max(dragDropSegments.length - 1, 0);

  const evidenceInstructions = useMemo(() => {
    if (typeof currentStepEvidenceConfig?.prompt === 'string' && currentStepEvidenceConfig.prompt.trim()) {
      return currentStepEvidenceConfig.prompt;
    }
    if (typeof currentStepEvidenceConfig?.instructions === 'string' && currentStepEvidenceConfig.instructions.trim()) {
      return currentStepEvidenceConfig.instructions;
    }
    return 'Students submit evidence for this step on their devices.';
  }, [currentStepEvidenceConfig]);

  const displayContent = useMemo(
    () => sanitizeStepContent(currentStep?.content),
    [currentStep?.content],
  );

  const currentStepRevealMode = useMemo(() => {
    const raw = String(currentStepSignalConfig?.reveal_mode || currentStepSignalConfig?.revealMode || 'auto')
      .trim()
      .toLowerCase();
    return raw || 'auto';
  }, [currentStepSignalConfig]);

  const answerKeyView = useMemo(
    () => buildAnswerKeyView(controlData?.display_step_teacher_answer_key ?? controlData?.current_step_teacher_answer_key),
    [controlData?.current_step_teacher_answer_key, controlData?.display_step_teacher_answer_key],
  );

  const currentStepLabel = currentStep
    ? `Step ${currentStep.order} of ${controlData?.steps.length || 0}`
    : 'Current step';
  const showSignalSection = Boolean(
    currentStep
    && !isSimulationActivity
    && (
      signalPrompt
      || signalOptions.length > 0
      || isGuidedSequenceSignal
      || isMatchingSignal
      || isOrderingSignal
      || isDragDropSignal
      || signalType
    ),
  );
  const expectedResponses = liveSignalStats?.total_students ?? controlData?.students.length ?? 0;
  const submittedResponses = liveSignalStats?.step_signal_submitted ?? 0;
  const currentGuidedCardIndex = Math.max(0, Math.min(guidedCardIndex, Math.max(guidedSequenceCards.length - 1, 0)));
  const currentGuidedCard = guidedSequenceCards[currentGuidedCardIndex] || null;

  const studioStepIds = useMemo(() => (
    controlData?.steps
      .filter((step) => step.runner_phase === 'studio')
      .map((step) => Number(step.id))
      .filter((stepId) => Number.isInteger(stepId)) || []
  ), [controlData]);
  const studioEntryStepId = Number(controlData?.run.studio_entry_step || studioStepIds[0] || 0);
  const studioEntryStepIndex = controlData?.steps.findIndex((step) => Number(step.id) === studioEntryStepId) ?? -1;
  const studioBoundaryStepId = studioEntryStepIndex > 0
    ? Number(controlData?.steps[studioEntryStepIndex - 1]?.id || 0)
    : studioEntryStepId;
  const studioBoundaryReached = studioEntryStepIndex === 0 || (
    Number(classStep?.id || 0) === studioEntryStepId
    || Number(classStep?.id || 0) === studioBoundaryStepId
  );
  const canEnterStudio = Boolean(
    controlData
    && studioStepIds.length > 0
    && controlData.run.status === 'active'
    && controlData.run.delivery_phase !== 'studio'
    && studioBoundaryReached
  );

  const refreshDisplay = useCallback(async () => {
    await loadControl({ silent: true });
  }, [loadControl]);

  useEffect(() => {
    setGuidedCardIndex(0);
  }, [currentStep?.id]);

  useEffect(() => {
    if (!runIdNumber) return undefined;
    return subscribeLessonRunSync(runIdNumber, () => {
      void refreshDisplay();
    });
  }, [refreshDisplay, runIdNumber]);

  const moveToStep = useCallback(async (direction: 'previous' | 'advance', targetStepId: number | null) => {
    if (!controlData || !targetStepId) return;
    try {
      setPendingAction(direction);
      setActionError(null);
      await lessonRunsApi.advance(controlData.run.id, { step_id: targetStepId });
      await refreshDisplay();
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch (err: any) {
      setActionError(err?.response?.data?.error || `Failed to ${direction === 'advance' ? 'advance step' : 'move to previous step'}.`);
    } finally {
      setPendingAction(null);
    }
  }, [controlData, refreshDisplay]);

  const revealCurrentStep = useCallback(async () => {
    if (!controlData || !currentStep?.id) return;
    try {
      setPendingAction('reveal');
      setActionError(null);
      await lessonRunsApi.reveal(controlData.run.id, { step_id: currentStep.id });
      await refreshDisplay();
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to reveal answer.');
    } finally {
      setPendingAction(null);
    }
  }, [controlData, currentStep?.id, refreshDisplay]);

  const updatePacingTimer = useCallback(async (
    action: 'start' | 'restart' | 'add' | 'end',
    seconds?: number,
  ) => {
    if (!controlData) return;
    try {
      setPacingTimerLoading(true);
      setPacingTimerError(null);
      await lessonRunsApi.pacingTimer(controlData.run.id, { action, ...(seconds ? { seconds } : {}) });
      await refreshDisplay();
      publishLessonRunSync(controlData.run.id, 'control_changed');
    } catch (err: any) {
      setPacingTimerError(err?.response?.data?.error || 'Failed to update the pacing timer.');
    } finally {
      setPacingTimerLoading(false);
    }
  }, [controlData, refreshDisplay]);

  const startIndependentWork = useCallback(async () => {
    if (!controlData || !canEnterStudio) return;
    try {
      setPendingAction('studio');
      setActionError(null);
      await lessonRunsApi.enterStudio(controlData.run.id);
      await refreshDisplay();
      publishLessonRunSync(controlData.run.id, 'step_changed');
    } catch {
      setActionError('Independent work could not be started. Return to Teach and try again.');
    } finally {
      setPendingAction(null);
    }
  }, [canEnterStudio, controlData, refreshDisplay]);

  const returnToLessonControl = useCallback(() => {
    if (!controlData) return;
    const displaySource = new URLSearchParams(location.search).get('source');
    if (focusTeacherOpener(displaySource, window.opener)) {
      window.close();
      return;
    }

    navigate(classroomRunId ? `/v2/teacher/teach?section=${controlData.run.section}` : `/v2/teacher/run/${controlData.run.id}`);
  }, [classroomRunId, controlData, location.search, navigate]);

  if (loading && !controlData) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-10 text-slate-700">
        Loading display…
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-10">
        <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Lesson run not available</h1>
          <p className="mt-2 text-sm text-slate-600">This display can no longer access the live lesson run.</p>
        </div>
      </div>
    );
  }

  if (error || !controlData) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-10 text-red-700">
        {error || 'No display data available.'}
      </div>
    );
  }

  const runActive = controlData.run.status === 'active';
  const showRevealButton = Boolean(
    currentStep
    && !isSimulationActivity
    && currentStepRevealMode === 'teacher'
    && !currentStepRevealed,
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#ffffff,_#e2e8f0_65%)] text-slate-900">
      {!contentOnly && <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Lesson display</p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {currentStepLabel}
              {currentStep?.title ? ` • ${currentStep.title}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={returnToLessonControl}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              {classroomRunId ? 'Back to Teach' : 'Back to Lesson Control'}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void moveToStep('previous', previousStep?.id ?? null)}
              disabled={!runActive || !previousStep || pendingAction !== null}
            >
              {pendingAction === 'previous' ? 'Changing…' : 'Previous Step'}
            </button>
            {canEnterStudio ? (
              <button
                type="button"
                className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => void startIndependentWork()}
                disabled={pendingAction !== null}
              >
                {pendingAction === 'studio' ? 'Starting…' : 'Start Independent Work'}
              </button>
            ) : (
              <button
                type="button"
                className="rounded bg-brand px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => nextStep ? void moveToStep('advance', nextStep.id) : onNextActivity?.()}
                disabled={!runActive || (!nextStep && !onNextActivity) || pendingAction !== null || transitionBusy}
              >
                {pendingAction === 'advance' || transitionBusy ? 'Changing…' : !classroomRunId ? 'Advance Step' : nextStep ? 'Next step' : nextActivityLabel || 'Next activity'}
              </button>
            )}
            {showRevealButton ? (
              <button
                type="button"
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => void revealCurrentStep()}
                disabled={!runActive || pendingAction !== null}
              >
                {pendingAction === 'reveal' ? 'Revealing…' : 'Reveal Answer'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      }
      <main className="mx-auto flex min-h-[calc(100vh-4.5rem)] w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6">
        {actionError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {actionError}
          </div>
        ) : null}

        {controlData.run.status === 'paused' ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This run is paused. Display controls are disabled until the run resumes.
          </div>
        ) : null}

        {controlData.run.status === 'completed' ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            This run has ended. The display is now read-only.
          </div>
        ) : null}

        {activeExplainBroadcast ? (
          <section className="rounded-3xl border border-indigo-200 bg-indigo-50 p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.24em] text-indigo-700">Teacher explanation</p>
            <div className="mt-3">
              <ExplainBroadcastContent markdown={String(activeExplainBroadcast.content_md || '')} />
            </div>
          </section>
        ) : null}

        {activePresentation ? (
          <section aria-label="Class presentation" className="overflow-hidden rounded-[2rem] border border-violet-200 bg-slate-950 shadow-[0_18px_60px_rgba(15,23,42,0.18)]">
            <img
              src={presentationSlideImageUrl(activePresentation.resource, activePresentation.slide)}
              alt={`${activePresentation.resource.title}, slide ${activePresentation.slide.slide_number}`}
              className="mx-auto max-h-[calc(100vh-10rem)] min-h-[60vh] w-full object-contain"
            />
            <div className="flex items-center justify-between gap-4 bg-white px-5 py-3 text-sm">
              <span className="font-medium text-slate-900">{activePresentation.resource.title}</span>
              <span className="text-slate-600">Selected slide {activePresentation.index + 1} of {activePresentation.slideCount}</span>
            </div>
          </section>
        ) : currentStep ? (
          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.08)] sm:p-8">
            <div className="mb-6 border-b border-slate-100 pb-6">
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-slate-500">
                {currentStep.stage_tag || 'Lesson'} &middot; {currentStepLabel}
              </p>
              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                {currentStep.title}
              </h1>
            </div>
            {currentStep.runner_phase !== 'studio' ? (
              <div className="mb-5">
                <PacingTimerCard
                  content={currentStep.content}
                  startedAt={currentStepStartedAt}
                  title={currentStep.title || `Step ${currentStep.order}`}
                  runtimeState={currentStepTimer as PacingTimerRuntimeState | null}
                  showControls={runActive && Boolean(currentStepTimer)}
                  actionLoading={pacingTimerLoading}
                  onAction={(action, seconds) => void updatePacingTimer(action, seconds)}
                />
                {pacingTimerError ? <p className="mt-2 text-xs text-rose-700">{pacingTimerError}</p> : null}
              </div>
            ) : null}
            {isSimulationActivity ? (
              <div className="rounded-3xl border border-cyan-200 bg-cyan-50 p-6">
                <p className="text-xs uppercase tracking-[0.24em] text-cyan-700">Simulation Activity</p>
                {simulationObjective ? (
                  <p className="mt-3 text-lg font-medium text-slate-900">{simulationObjective}</p>
                ) : null}
                <p className="mt-3 text-sm text-cyan-950">
                  Students complete this simulation on their devices.
                </p>
              </div>
            ) : (
              <StepRenderer
                title={currentStep.title}
                stageTag={currentStep.stage_tag}
                stepKind={currentStep.step_kind}
                content={displayContent}
                hideHeader
              />
            )}
          </section>
        ) : (
          <section className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-600 shadow-sm">
            Waiting for the next step.
          </section>
        )}

        {showSignalSection ? (
          <section className="rounded-3xl border border-sky-200 bg-sky-50 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-sky-700">Quick check</p>
                <p className="mt-1 text-base text-sky-950">Respond on your device.</p>
              </div>
              <p className="text-base font-semibold text-sky-950" aria-live="polite">
                {submittedResponses} of {expectedResponses} responded
              </p>
            </div>
            {signalPrompt ? (
              <div className="mt-4 text-base text-slate-900">
                <ReactMarkdown components={markdownComponents}>
                  {signalPrompt}
                </ReactMarkdown>
              </div>
            ) : null}

            {isMcqSignal && signalOptions.length > 0 ? (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {signalOptions.map((option, index) => (
                  <div
                    key={`display-option-${index}`}
                    className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-900"
                  >
                    <span className="font-semibold text-sky-800">{index + 1}.</span> {option}
                  </div>
                ))}
              </div>
            ) : null}

            {isMatchingSignal && (matchingLeftItems.length > 0 || matchingRightItems.length > 0) ? (
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-sky-200 bg-white p-4">
                  <p className="text-xs uppercase tracking-wide text-sky-700">Match from</p>
                  <div className="mt-3 space-y-2">
                    {matchingLeftItems.map((item, index) => (
                      <div key={`display-left-${index}`} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-sky-200 bg-white p-4">
                  <p className="text-xs uppercase tracking-wide text-sky-700">Available matches</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {matchingRightItems.map((item, index) => (
                      <span
                        key={`display-right-${index}`}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-900"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {isOrderingSignal && orderingItems.length > 0 ? (
              <div className="mt-4 rounded-2xl border border-sky-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-sky-700">Order these items</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {orderingItems.map((item, index) => (
                    <span
                      key={`display-order-${index}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-900"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {isDragDropSignal && (dragDropTarget || dragDropTokens.length > 0) ? (
              <div className="mt-4 space-y-4 rounded-2xl border border-sky-200 bg-white p-4">
                {dragDropTarget ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-900">
                    {dragDropSegments.map((segment, index) => (
                      <React.Fragment key={`drag-display-segment-${index}`}>
                        <span className="whitespace-pre-wrap">{segment}</span>
                        {index < dragDropBlankCount ? (
                          <span className="mx-1 inline-flex w-20 border-b-2 border-dashed border-slate-400 align-middle" />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                ) : null}
                {dragDropTokens.length > 0 ? (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-sky-700">Token bank</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {dragDropTokens.map((token, index) => (
                        <span
                          key={`display-token-${index}`}
                          className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-900"
                        >
                          {token}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isGuidedSequenceSignal ? (
              <div className="mt-4 space-y-4 rounded-2xl border border-sky-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs uppercase tracking-wide text-sky-700">Guided sequence</p>
                  <p className="text-xs text-slate-500">
                    {guidedSequenceCards.length > 0
                      ? `${guidedSequenceCards.length} prompts`
                      : 'No prompts configured'}
                  </p>
                </div>
                {guidedSequenceCards.length === 0 ? (
                  <p className="text-sm text-amber-700">
                    No guided sequence cards are configured for this step.
                  </p>
                ) : (
                  currentGuidedCard ? (
                    <article
                      key={`guided-sequence-card-${currentGuidedCard.key}`}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-sky-700">
                            Prompt {currentGuidedCardIndex + 1} of {guidedSequenceCards.length}
                          </p>
                          <p className="mt-2 text-sm font-medium text-slate-900">{currentGuidedCard.prompt}</p>
                        </div>
                        {currentGuidedCard.required ? (
                          <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-800">
                            Required
                          </span>
                        ) : null}
                      </div>
                      {currentGuidedCard.template ? (
                        <pre className="mt-4 overflow-x-auto rounded-xl border border-sky-100 bg-white p-3 text-xs text-slate-900 whitespace-pre-wrap">
                          <code>{currentGuidedCard.template}</code>
                        </pre>
                      ) : null}
                      <div className="mt-4 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="rounded border border-sky-200 bg-white px-3 py-1.5 text-sm text-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => setGuidedCardIndex((prev) => Math.max(0, prev - 1))}
                          disabled={currentGuidedCardIndex === 0}
                        >
                          Previous Prompt
                        </button>
                        <button
                          type="button"
                          className="rounded border border-sky-200 bg-white px-3 py-1.5 text-sm text-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => setGuidedCardIndex((prev) => Math.min(guidedSequenceCards.length - 1, prev + 1))}
                          disabled={currentGuidedCardIndex >= guidedSequenceCards.length - 1}
                        >
                          Next Prompt
                        </button>
                      </div>
                    </article>
                  ) : null
                )}
              </div>
            ) : null}

          </section>
        ) : null}

        {currentStep?.counts_as_evidence && !isSimulationActivity ? (
          <section className="rounded-3xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.24em] text-amber-700">Evidence</p>
            <div className="mt-3 text-base text-slate-900">
              <ReactMarkdown components={markdownComponents}>
                {evidenceInstructions}
              </ReactMarkdown>
            </div>
            <p className="mt-4 text-sm font-medium text-amber-900">Complete this task on your device.</p>
          </section>
        ) : null}

        {currentStepRevealed && answerKeyView && !isSimulationActivity ? (
          <section className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">Expected answer</p>
            <div className="mt-3">{answerKeyView}</div>
          </section>
        ) : null}
      </main>
    </div>
  );
};
